import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';
import { Info } from './Info.js';

/**
 * Einrichtung des Rücklaufkanals.
 *
 * Ohne ihn ist die Zustellbarkeit blind: SMTP meldet nur, was bei der
 * Übergabe schiefgeht. Der harte Bounce Stunden später und die Beschwerde
 * über den Feedback-Loop kommen ausschließlich vom Versanddienst — und ohne
 * sie bleibt die Bounce-Rate jedes Absenders bei null, weshalb auch die
 * automatische Notbremse nie auslösen kann.
 *
 * Zwei Wege, zwei Geheimnisse:
 *
 *   - Elastic Email ruft nur eine Adresse auf. Der Nachweis steckt deshalb im
 *     Pfad. Das ist schwächer als eine Signatur, weil die Adresse in fremden
 *     Protokollen landet — darum ein eigener Wert, getrennt rotierbar.
 *   - Eine selbstgebaute Brücke signiert mit HMAC. Der Wert dafür darf
 *     niemals in einer URL stehen.
 */

interface SecretState {
  configured: boolean;
  masked: string | null;
  path?: string;
}

/**
 * Wert zum Mitnehmen — gleiche Bauweise wie der DNS-Eintrag auf derselben
 * Seite. Ohne Zwischenablage bleibt der Wert lesbar stehen: nicht kopiert
 * ist nicht verloren.
 */
function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="suggestion">
      <div>
        <span className="field__label">{label}</span>
        <code className="check__record">{value}</code>
      </div>
      <Button variant="secondary" onClick={copy}>
        {copied ? 'Kopiert' : 'Kopieren'}
      </Button>
    </div>
  );
}

export function WebhookSetup() {
  return (
    <section className="card">
      <div className="card__body stack">
        <div>
          <h2 className="check__head" style={{ fontSize: 'var(--text-lg)' }}>
            <span>Rücklauf vom Versanddienst</span>
            <Info zu="ruecklauf" />
          </h2>
          <p className="muted text-sm">
            Bounces und Beschwerden kommen nicht über SMTP zurück, sondern über einen Webhook.
            Solange hier nichts eingerichtet ist, bleibt die Bounce-Rate jedes Absenders bei null
            — und die Notbremse löst nie aus, weil sie nichts zu messen hat.
          </p>
        </div>

        <ProviderToken />
        <SigningSecret />
      </div>
    </section>
  );
}

function ProviderToken() {
  const queryClient = useQueryClient();
  const [fresh, setFresh] = useState<{ path: string } | null>(null);

  const state = useQuery({
    queryKey: ['provider-webhook-token'],
    queryFn: ({ signal }) =>
      api.get<SecretState>('/api/project/provider-webhook-token', signal),
  });

  const rotate = useMutation({
    mutationFn: () =>
      api.post<{ token: string; path: string }>('/api/project/provider-webhook-token', {}),
    onSuccess: (data) => {
      setFresh({ path: data.path });
      void queryClient.invalidateQueries({ queryKey: ['provider-webhook-token'] });
    },
  });

  return (
    <article className="check" data-status={state.data?.configured ? 'ok' : 'warn'}>
      <header className="check__head">
        <strong>Elastic Email</strong>
        <span className="badge" data-check={state.data?.configured ? 'ok' : 'warn'}>
          {state.data?.configured ? 'eingerichtet' : 'fehlt'}
        </span>
      </header>

      <p className="muted text-sm">
        {state.data?.configured
          ? `Token hinterlegt (${state.data.masked ?? '—'}). Neu erzeugen macht die alte Adresse ungültig.`
          : 'Noch kein Token. Ohne eines lehnt der Endpunkt jede Meldung ab, statt sie durchzuwinken.'}
      </p>

      {fresh && (
        <div className="alert" data-tone="success" role="status">
          <p>
            Diese Adresse bei Elastic Email als Webhook eintragen. Sie wird{' '}
            <strong>nie wieder angezeigt</strong>.
          </p>
          <CopyValue label="Webhook-Adresse" value={`${window.location.origin}${fresh.path}`} />
        </div>
      )}

      <div className="dialog__actions">
        <Button variant="secondary" onClick={() => rotate.mutate()} loading={rotate.isPending}>
          {state.data?.configured ? 'Token neu erzeugen' : 'Token erzeugen'}
        </Button>
      </div>

      {rotate.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {rotate.error instanceof Error ? rotate.error.message : 'Erzeugen fehlgeschlagen.'}
        </div>
      )}
    </article>
  );
}

function SigningSecret() {
  const queryClient = useQueryClient();
  const [fresh, setFresh] = useState<{ secret: string; path: string } | null>(null);

  const state = useQuery({
    queryKey: ['webhook-secret'],
    queryFn: ({ signal }) => api.get<SecretState>('/api/project/webhook-secret', signal),
  });

  const rotate = useMutation({
    mutationFn: () =>
      api.post<{ secret: string; path: string }>('/api/project/webhook-secret', {}),
    onSuccess: (data) => {
      setFresh({ secret: data.secret, path: data.path });
      void queryClient.invalidateQueries({ queryKey: ['webhook-secret'] });
    },
  });

  return (
    <article className="check" data-status={state.data?.configured ? 'ok' : 'warn'}>
      <header className="check__head">
        <strong>Eigene Brücke (signiert)</strong>
        <span className="badge" data-check={state.data?.configured ? 'ok' : 'warn'}>
          {state.data?.configured ? 'eingerichtet' : 'fehlt'}
        </span>
      </header>

      <p className="muted text-sm">
        Für einen anderen Dienst oder ein eigenes Skript: HMAC-SHA256 über den rohen
        Anfragerumpf, hex-kodiert, in der Kopfzeile <code>X-Mailflow-Signature</code>.
        {state.data?.configured && ` Hinterlegt: ${state.data.masked ?? '—'}.`}
      </p>

      {fresh && (
        <div className="alert" data-tone="success" role="status">
          <p>
            Das Secret erscheint <strong>genau einmal</strong>. Wer es verliert, rotiert —
            anzeigen lässt es sich nicht wieder.
          </p>
          <CopyValue label="Secret" value={fresh.secret} />
          <CopyValue label="Zieladresse" value={`${window.location.origin}${fresh.path}`} />
        </div>
      )}

      <div className="dialog__actions">
        <Button variant="secondary" onClick={() => rotate.mutate()} loading={rotate.isPending}>
          {state.data?.configured ? 'Secret rotieren' : 'Secret erzeugen'}
        </Button>
      </div>

      {rotate.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {rotate.error instanceof Error ? rotate.error.message : 'Erzeugen fehlgeschlagen.'}
        </div>
      )}
    </article>
  );
}
